import { useState, SyntheticEvent, useEffect } from 'react';
import { useRouter } from 'next/router';

import { get, post, destroy, patch } from 'infras/rest_client';
import { ViewerItem } from 'presentations/api/schema/user';
import {
  apiPath,
  serviceListPath,
  servicePaymentPath,
} from 'presentations/routes/path';
import { BusinessProfileItem } from 'presentations/api/schema/business_profile';
import { IntegrationMergedTeamShopAccountItem } from 'presentations/api/schema/team_shop_account';
import { ShopAccountStatus } from 'domains/models/team_shop_account/status';
import { setEvent } from 'presentations/views/helpers/analytics';

interface Props {
  viewer: ViewerItem | null;
}

export function useBusinessProfilePage(props: Props) {
  const router = useRouter();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isOnboard, setIsOnboard] = useState<boolean>(false);
  const [isOpenOnboard, setIsOpenOnboard] = useState<boolean>(false);
  const [isOpen, setIsOpen] = useState<boolean>(router.query.isOpen === 'true');
  const [
    businessProfile,
    setBusinessProfile,
  ] = useState<BusinessProfileItem | null>(null);
  const [
    shopAccount,
    setShopAccount,
  ] = useState<IntegrationMergedTeamShopAccountItem | null>(null);

  useEffect(() => {
    (async () => {
      if (props.viewer) {
        setIsLoading(true);
        const shopAccountResult = await get(
          apiPath(`/v1/teams/${props.viewer.team?.uid}/shop_account`),
        );
        if (shopAccountResult.isSuccess) {
          const shopAccount = shopAccountResult.data;
          setShopAccount(shopAccount);
          if (
            router.query.onboard === '1' &&
            shopAccount &&
            shopAccount.status === ShopAccountStatus.Enable &&
            !shopAccount.isConnect
          ) {
            const shopAccountResult = await patch(
              apiPath(
                `/v1/teams/${props.viewer.team?.uid}/shop_account/connect`,
              ),
            );
          }
          if (
            router.query.onboard === '1' &&
            shopAccount &&
            shopAccount.status === ShopAccountStatus.Disable
          ) {
            router.push(servicePaymentPath());
            return;
          }
        }

        const findResult = await get(
          apiPath(`/v1/teams/${props.viewer.team?.uid}/business_profile`),
        );
        if (!findResult.isSuccess) {
          setIsLoading(false);
          if (findResult.status === 404) {
            setIsOpen(true);
          }
          return;
        }
        setBusinessProfile(findResult.data);
        setIsLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (router.isReady) {
      const onboard = Boolean(router.query.onboard);
      setIsOnboard(onboard);
      setIsOpenOnboard(onboard);
    }
    setEvent('return from stripe account link');
  }, [router, shopAccount]);

  const onSave = () => {
    if (isOnboard) {
      router.push(serviceListPath());
    } else {
      setIsOpen(false);
    }
  };
  const onCancel = () => {
    if (businessProfile) {
      setIsOpen(false);
    } else {
      router.push(serviceListPath());
    }
  };
  const onEdit = () => {
    setIsOpen(true);
  };

  const onCloseOnboard = () => {
    setIsOpenOnboard(false);
  };

  return {
    state: {
      businessProfile,
      shopAccount,
      isLoading,
      isOpen,
      isOpenOnboard,
    },
    actions: {
      onCancel,
      onCloseOnboard,
      onSave,
      onEdit,
    },
  };
}
